import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  API_BASE_URL,
  apiRequest,
  clearSessionToken,
  getSessionToken,
  setSessionToken,
} from '../lib/api-client';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  username: string;
  role: string;
  company?: {
    id: string;
    companyName: string;
  } | null;
}

export const isAdminRole = (role?: string) =>
  role === 'admin' || role === 'superadmin';

export const isSuperadmin = (role?: string) => role === 'superadmin';

export const isCompanyRole = (role?: string) => role === 'company';

interface ApiAuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ sessionToken?: string; user?: User }>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const ApiAuthContext = createContext<ApiAuthContextType | undefined>(undefined);

export const ApiAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const sessionToken = getSessionToken();
      if (!sessionToken) {
        return;
      }

      const userData = await apiRequest<{ user: User }>('/auth/me', {
        auth: true,
      });
      setUser(userData.user);
    } catch (error) {
      console.error('Auth check failed:', error);
      clearSessionToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const result = await apiRequest<{ sessionToken?: string; user: User; error?: string }>(
        '/auth/login',
        {
          method: 'POST',
          body: JSON.stringify({ email, password }),
        }
      );

      if (result.sessionToken) {
        setSessionToken(result.sessionToken);
        setUser(result.user);
      }

      return result;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      const sessionToken = getSessionToken();
      if (sessionToken) {
        await apiRequest('/auth/logout', {
          method: 'POST',
          auth: true,
        }).catch((error) => {
          console.error('Logout error:', error);
        });
      }
    } finally {
      clearSessionToken();
      setUser(null);
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return (
    <ApiAuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </ApiAuthContext.Provider>
  );
};

export const useApiAuth = () => {
  const context = useContext(ApiAuthContext);
  if (context === undefined) {
    throw new Error('useApiAuth must be used within an ApiAuthProvider');
  }
  return context;
};
